<template>
  <div class="products-quantities-selector-v3">
    <div class="products-matrix">
      <div class="products">
        <div v-if="!isQuantityIncreaseAllowed" class="warning-banner">
          {{ $t('matrix.increase_quantities_is_not_allowed') }}
        </div>

        <!-- Quantities Navigation Tab -->
        <div
          v-if="navBarItems.length"
          class="navbar-background"
          id="quantitiesNavBar"
          :style="`top: ${mainGroupGenderNavBarStickyOffset}px`">
          <QuantitiesNavBar
            ref="quantitiesNavBar"
            :items="navBarItems"
            :show-wholesale-price="showWholesalePrice"
            :show-product-matrix-group-by-dropdown="showProductMatrixGroupByDropdown"
            :product-matrix-group-by-options="productMatrixGroupByOptions"
            v-on:toggle-show-wholesale-price="toggleShowWholesalePrice"
            v-on:product-matrix-group-by="productMatrixGroupByOption"
            :show-quantities="true"
            @selected="onNavBarSelected" />
        </div>

        <!-- Loader -->
        <div v-if="loading" class="loader"></div>
        <div v-else-if="error" class="error-message">{{ $t('matrix.error.no_products_available') }}</div>

        <div v-else class="product-groups-cnt">
          <!-- Main Group/Gender/Product Group Matrix Rendering -->
          <template v-for="[groupGenderKey, groupGender] in groupedMatrix" :key="groupGenderKey">
            <template v-if="`${groupGenderKey}` === selectedNavId">
              <ProductGroupMatrix
                class="product-group-cnt"
                v-for="[productGroupKey, productGroup] in groupGender.productGroups"
                :key="productGroupKey"
                :is-quantity-increase-allowed="isQuantityIncreaseAllowed"
                :product-group="productGroup"
                :default-size="defaultSize"
                :products-quantities="calculatedTotals.productTotals"
                :fabric-totals="calculatedTotals.fabricTotals"
                :product-group-totals="calculatedTotals.productGroupTotals"
                :show-wholesale-price="showWholesalePrice"
                @show-product-model-details="onShowProductModelDetails"
                @remove-product-group-quantities="onRemoveProductGroupQuantities(groupGender.name, productGroup.name)"
                @matrix-input-change="onProductQuantityChange" />
            </template>
          </template>

          <!-- Totals Display -->
          <TotalsDisplay
            :fabric-totals="calculatedTotals.fabricTotals"
            :main-group-totals="calculatedTotals.mainGroupTotals"
            :matrix-total="calculatedTotals.matrixTotal"
            :overall-total="overallTotal"
            :show-wholesale-price="showWholesalePrice"
            :show-availability-legends="showAvailabilityLabel" />
        </div>
      </div>
    </div>

    <!-- Product Model Details Popup -->
    <div v-if="productModelDetails">
      <NotificationMessage :size="'full'" class="popup-new" v-on:close="closeProductModelDetailsPopup">
        <ViewProductModelDetailsV2
          :hide-select-button="true"
          :is-selected="false"
          :product-model="productModelDetails"
          v-on:close="closeProductModelDetailsPopup" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  MatrixType,
  OrderType,
  ProductGender,
  ProductMainGroups,
  type ProductMatrixQueryDto,
} from '@onrunning/backstage-models';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NotificationMessage from '@/components/common/NotificationMessage.vue';
import QuantitiesNavBar, { type NavBarItem } from '@/components/common/QuantitiesNavBar.vue';
import { useFormat } from '@/composables/format';
import { useGroupedMatrix } from '@/composables/useGroupedMatrix';
import { useMatrixProduct } from '@/composables/useMatrixProduct';
import { useNotification } from '@/composables/useNotification';
import { useOrderValidation } from '@/composables/useOrderValidation';
import { globalConstants } from '@/constants/global.constants';
import { genderLetterMap } from '@/constants/matrix.constants';
import type { OrderEditType } from '@/constants/orderEditType';
import { utilsMixins } from '@/mixins/utils.mixins';
import { FeaturesService } from '@/services/features.service';
import { ProductsDetailsService } from '@/services/products-details.service';
import { ProductsMatrixService } from '@/services/products-matrix.service';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useProductPricesStore } from '@/stores/product-prices';
import { useUserStore } from '@/stores/user';
import type { Fabric, MatrixTotals, ProductMatrixApiResponse, ProductModelDetails } from '@/types/matrix.interface';
import { ProductMatrixGroupByOptions } from '@/types/matrix.interface';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import ViewProductModelDetailsV2 from '../products/ViewProductModelDetailsV2.vue';
import ProductGroupMatrix from './ProductGroupMatrix.vue';
import TotalsDisplay from './TotalsDisplay.vue';

interface Props {
  isExistingOrder?: boolean;
  seasonStart?: string;
  orderNumber?: string;
  overallTotal?: number;
  isQuantityIncreaseAllowed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isQuantityIncreaseAllowed: true,
});

const productsMatrixService = new ProductsMatrixService();
const productsDetailsService = new ProductsDetailsService();

const { getProductPrices } = useProductPricesStore();
const { shouldLoadPricesOnProductMatrix } = storeToRefs(useProductPricesStore());
const { orderType } = storeToRefs(useOrderStore());

const { t } = useI18n();
const userStore = useUserStore();
const { notifyError } = useNotification();
const productMatrixStore = useProductMatrixStore();
const route = useRoute();
const { isDraftEditMode } = useOrderValidation(route.params.editType as OrderEditType);
const { formatNonLocalisedDateString } = useFormat();

const error = ref(false);
const loading = ref(false);
const selectedNavId = ref<string | null>(null);
const productModelDetails = ref<ProductModelDetails | null>(null);
const productMatrix = ref<ProductMatrixApiResponse | null>(null);
const showWholesalePrice = ref(true);
const showProductMatrixGroupByDropdown = computed((): boolean => {
  return (
    FeaturesService.enabledShipDatesGrouping &&
    orderType.value !== null &&
    [OrderType.PreOrder, OrderType.MultiplePreOrder].includes(orderType.value)
  );
});
const productMatrixGroupBy = ref<ProductMatrixGroupByOptions>(ProductMatrixGroupByOptions.Model);
const productMatrixGroupByOptions = ref([ProductMatrixGroupByOptions.Model, ProductMatrixGroupByOptions.ShippingDate]);
const productGroupNumbers = ref<string[]>([]);

const { onProductQuantityChange, onRemoveProductGroupQuantities, updateMatrixTotal, matrixType } = useMatrixProduct();

const selectedDate = computed((): string | undefined => {
  const orderDate = productMatrixStore.activeMatrixOrder?.date;
  if (!orderDate) {
    return;
  }

  return formatNonLocalisedDateString(orderDate);
});

const { groupedMatrix, showAvailabilityLabel } = useGroupedMatrix(
  productMatrix,
  computed(() => ({
    orderDate: selectedDate.value,
    matrixType: matrixType.value,
    seasonStart: props.seasonStart,
    isExistingOrder: props.isExistingOrder,
  })),
);

const defaultSize = computed((): string => {
  const defaultSize = t(`config.default_size_run['${userStore.selectedAccount?.billingCountry}']`);

  if (globalConstants.sizeTypes.includes(defaultSize)) {
    return defaultSize;
  }

  return globalConstants.defaultSizeType;
});

const navBarItems = computed<NavBarItem[]>(() => {
  const items: NavBarItem[] = [];
  for (const [mainGroupKey] of groupedMatrix.value) {
    items.push({
      id: mainGroupKey,
      title: t(`matrix.tab_name.${mainGroupKey}`),
      selected: selectedNavId.value === mainGroupKey,
      total: calculatedTotals.value.groupGenderTotals[mainGroupKey] ?? 0,
    });
  }

  return items.sort((a, b) => b.title.localeCompare(a.title));
});

const calculatedTotals = computed((): MatrixTotals => {
  const productTotals: Record<string, number> = {};
  const fabricTotals: Record<string, number> = {};
  const productGroupTotals: Record<string, number> = {};
  const mainGroupTotals: Record<string, number> = {};
  const groupGenderTotals: Record<string, number> = {};

  let matrixTotal = 0;

  for (const { productId, quantity } of productMatrixStore.selectedProducts) {
    const product = productMatrixStore.products[productId];
    if (!product) continue;

    const productGroupKey = `${product.gender}-${product.productGroupName}`;
    const groupGenderKey =
      product.mainGroup === ProductMainGroups.accessories
        ? `${product.mainGroup}-${genderLetterMap[ProductGender.unisex]}`
        : `${product.mainGroup}-${genderLetterMap[product.gender]}`;

    productTotals[product.productId] = quantity ?? 0;
    fabricTotals[product.productGroupNumber] = (fabricTotals[product.productGroupNumber] ?? 0) + quantity;

    if (product?.disabled) {
      continue;
    }

    productGroupTotals[productGroupKey] = (productGroupTotals[productGroupKey] ?? 0) + quantity;
    groupGenderTotals[groupGenderKey] = (groupGenderTotals[groupGenderKey] ?? 0) + quantity;
    mainGroupTotals[product.mainGroup] = (mainGroupTotals[product.mainGroup] ?? 0) + quantity;
    matrixTotal += quantity;
  }

  return {
    matrixTotal,
    fabricTotals,
    productGroupTotals,
    mainGroupTotals,
    productTotals,
    groupGenderTotals,
  };
});

const mainGroupGenderNavBarStickyOffset = computed((): number => {
  return utilsMixins.calculateDivHeight(['#addEditNavBar', '#matrixRelatedOrdersSelector']);
});

watch(
  navBarItems,
  (items) => {
    if (items.length && !selectedNavId.value) {
      selectedNavId.value = items[0].id;
    }
  },
  { once: true },
);

watch(
  () => calculatedTotals.value.matrixTotal,
  (newTotal) => {
    updateMatrixTotal(newTotal);
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchProductMatrix();

  if (isDraftEditMode.value) {
    productMatrixStore.updateDateIfLaunchDateMoveToLater();
  }
});

watch(selectedDate, (newDate) => {
  if (newDate && (matrixType.value === MatrixType.order || matrixType.value === MatrixType.lastSeason)) {
    fetchProductMatrix();
  }
});

async function fetchProductMatrix(): Promise<void> {
  try {
    if (!selectedDate.value) {
      return;
    }

    const productMatrixQuery = {
      itemGroupNames: Array.from(productMatrixStore.selectedProductModelsNames),
      orderDate: selectedDate.value,
      matrixType: matrixType.value,
      orderNumber: props.orderNumber,
    } as ProductMatrixQueryDto;

    loading.value = true;

    const result = await productsMatrixService.getProductsMatrixV3(userStore.selectedAccount.id, productMatrixQuery);

    if (!isProductMatrixValid(result)) {
      throw new Error(t('matrix.error.no_products_available'));
    }

    productGroupNumbers.value = [...new Set(result?.products.map((product) => product.productGroupNumber) ?? [])];

    if (productGroupNumbers.value.length > 0 && shouldLoadPricesOnProductMatrix.value) {
      getProductPrices({
        productGroupNumbers: productGroupNumbers.value,
        requestReceiptDate: selectedDate.value,
        orderType: orderType.value!,
      });
    }

    productMatrix.value = result;
    error.value = false;
  } catch (err) {
    error.value = true;
    notifyError(t('global.error'));
  } finally {
    loading.value = false;
  }
}

const isProductMatrixValid = (result: ProductMatrixApiResponse | null): boolean => {
  if (!result?.products?.length || !result?.sizeGroups?.length) {
    return false;
  }

  return true;
};

const onNavBarSelected = (itemId: string) => {
  selectedNavId.value = itemId;
};

const onShowProductModelDetails = async (product: Fabric) => {
  const imagesResult = await productsDetailsService.getProductModelImages(product.id);

  if (imagesResult.isValid() && imagesResult.model && imagesResult.model.length > 0) {
    productModelDetails.value = {
      name: product.name,
      defaultVariantImages: imagesResult.model,
      defaultImage: imagesResult.model[0],
    };
  }
};

const closeProductModelDetailsPopup = () => {
  productModelDetails.value = null;
};

const toggleShowWholesalePrice = () => {
  showWholesalePrice.value = !showWholesalePrice.value;
};

const productMatrixGroupByOption = (option: ProductMatrixGroupByOptions) => {
  console.log('Selected group-by option:', option);
  productMatrixGroupBy.value = option;
};
</script>

<style lang="scss" scoped>
.products-quantities-selector-v3 {
  .products-matrix {
    max-width: unset;
    padding: 0;
    width: 100%;
  }

  .is-invalid {
    border: none !important;
    color: var(--special-red-color-darker) !important;

    input::placeholder {
      color: var(--special-red-color-darker) !important;
    }
  }

  .warning-banner {
    padding: 20px;
    margin-bottom: 20px;
    color: var(--primary-text-color);
    background-color: var(--special-orange-color);

    @include breakpoint(medium) {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .navbar-background {
    @include navbar-background;

    top: 83px;

    @include breakpoint(medium) {
      top: 63px;
    }

    @include breakpoint(small) {
      top: 173px;
    }

    &.multiple {
      top: 168.8px;
    }
  }

  .product-groups-cnt {
    @include common-container;
    padding: 16px 20px;

    @include breakpoint(medium) {
      padding: 20px 2px;
    }
  }

  .product-group-cnt {
    margin-bottom: 10px;
    background-color: var(--primary-bg-color);

    @include breakpoint(medium) {
      font-size: 14px;
      padding: 0;
    }
  }
}
</style>
